import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ThemeProvider,
  CssBaseline,
  Box,
  Container,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Typography,
  Button,
} from "@mui/material";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import theme from "./theme.ts";
import PlanSelector from "./PlanSelector.tsx";
import PaymentModal from "./PaymentModal.tsx";
import CurrentPlanCard from "./CurrentPlanCard.tsx";
import type {
  Plan,
  PaymentFormData,
  Restaurant,
  ActiveSubscription,
  LastPayment,
} from "./types";
import TopHeader from "../../components/TopHeader.tsx";
import axios from "axios";
import jwtDecode from "jwt-decode";

// Simulated data per restaurant
const RESTAURANT_SUBSCRIPTIONS: Record<
  string,
  { subscription: ActiveSubscription; lastPayment: LastPayment } | null
> = {
  r1: {
    subscription: {
      planId: 2,
      planName: "Standard",
      billingCycle: "MONTHLY",
      startDate: "2026-03-01",
      nextBillingDate: "2026-05-01",
      amount: 1007.5,
      status: "active",
    },
    lastPayment: {
      id: "pay_001",
      date: "2026-04-01",
      amount: 1007.5,
      planName: "Standard",
      billingCycle: "MONTHLY",
      cardLast4: "4242",
      status: "success",
    },
  },
  r2: {
    subscription: {
      planId: 1,
      planName: "Basic",
      billingCycle: "YEARLY",
      startDate: "2026-01-15",
      nextBillingDate: "2027-01-15",
      amount: 4995,
      status: "active",
    },
    lastPayment: {
      id: "pay_002",
      date: "2026-01-15",
      amount: 4995,
      planName: "Basic",
      billingCycle: "YEARLY",
      cardLast4: "8910",
      status: "success",
    },
  },
  r3: null,
};

const SubscriptionSetup: React.FC = () => {
  const [restaurants, setRestaurants] = useState<any[]>();
  const [selectedRestaurant, setSelectedRestaurant] = useState<string>();
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [billingCycle, setBillingCycle] = useState();
  const [billingCycles, setBillingCycles] = useState();
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [PLANS_DATA, setPLANS_DATA] = useState<Plan[]>([]);
  const [allFeatures, setAllFeatures] = useState();

  const [restaurant, setRestaurant] = useState();
  const [subData, setSubData] = useState(null);
  const [currentPlanId, setCurrentPlanId] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<Plan | undefined>(undefined);
  const [currentPlan, setCurrentPlan] = useState<Plan | undefined>(undefined);

  const handleSelectPlan = (planId: number, pricing: any) => {
    if (planId === currentPlanId) return;
    setSelectedPlanId(planId);
    setSelectedPlan(PLANS_DATA.find((p) => p.planId === planId));
    setPaymentModalOpen(true);
  };

  const handlePaymentSubmit = (_data: PaymentFormData) => {
    setPaymentModalOpen(false);
    setSuccessOpen(true);
    setSelectedPlanId(null);
    setSelectedPlan(null);
  };

  const loadDataForRestaurant = async () => {
    const token = localStorage.getItem("gotasteadmintoken");
    var decodedData = {};
    if (token != null) {
      try {
        decodedData = jwtDecode(token);

        var obj = {
          id: decodedData.UserId,
          title: decodedData.UserName,
        };
      } catch (error) {}
    }
    const restaurant = JSON.parse(localStorage.getItem("parentrestaurant"));
    axios
      .get(
        process.env.REACT_APP_SECRET +
          "/api/Restaurant/getrestaurantdetailsbyidfortable/" +
          decodedData.UserId +
          "/" +
          restaurant.id,
      )
      .then((response) => {
        console.log(response.data);
        const list: any[] = [];
        response.data.map((dt, i) => {
          var obj = {
            id: dt.id,
            name: dt.name,
            address: dt.addressLine1,
            PhoneNumber: dt.phoneNumber,
            Email: dt.emailAddress,
          };
          list.push(obj);
        });
        const id = JSON.parse(localStorage.getItem("restaurant"))?.id;
        setRestaurants(list);
        setSelectedRestaurant(id);
        setRestaurant(list.find((r) => r.id === id));
      });
  };

  const loadSubscriptionData = (restaurantId: string) => {
    // Implementation for loading subscription data for a specific restaurant

    axios
      .get(
        `${process.env.REACT_APP_SECRET}/api/SubscriptionPlan/subscriptionplan-get-by-restaurant/${restaurantId}`,
      )
      .then((response) => {
        setSubData(response.data.data);
        setCurrentPlanId(response.data.data?.planID);
      })
      .catch((error) => {
        console.error("Error fetching subscription data:", error);
        setSubData(null);
        setCurrentPlanId(null);
      });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [res1, res2, res3] = await Promise.all([
          axios.get(
            process.env.REACT_APP_SECRET +
              "/api/SubscriptionPlan/get-all-used-billing-cycles",
          ),
          axios.get(
            process.env.REACT_APP_SECRET +
              "/api/SubscriptionPlan/get-all-subscription-plan",
          ),
          axios.get(
            process.env.REACT_APP_SECRET +
              "/api/SubscriptionPlan/get-all-used-features",
          ),
        ]);
        setPLANS_DATA(res2.data.data);
        setBillingCycles(res1.data.data);
        setBillingCycle(res1.data.data?.[0]?.displayName || "");
        setAllFeatures(res3.data.data);
      } catch (err) {
        console.error("Error fetching plans:", err);
      }
    };
    const id = JSON.parse(localStorage.getItem("restaurant"))?.id;
    fetchData();
    loadDataForRestaurant();
    loadSubscriptionData(id);
  }, []);

  useEffect(() => {
    loadSubscriptionData(selectedRestaurant);
  }, [selectedRestaurant]);

  return (
    <ThemeProvider theme={theme}>
      {/* <TopHeader /> */}
      <CssBaseline />
      <Box
        sx={{ minHeight: "100vh", bgcolor: "#faf8f6", py: { xs: 3, md: 5 } }}
      >
        <Container maxWidth="lg">
          {/* Restaurant selector */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                bgcolor: "#371b17",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <RestaurantIcon sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <InputLabel>Select Restaurant</InputLabel>
              <Select
                value={selectedRestaurant || ""}
                label="Select Restaurant"
                onChange={(e) => {
                  setSelectedRestaurant(e.target.value);
                  setSelectedPlanId(null);
                  setPaymentModalOpen(false);
                  setRestaurant(
                    restaurants.find((r) => r.id === e.target.value),
                  );
                }}
                sx={{ borderRadius: 3 }}
              >
                {restaurants?.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Current plan & last payment */}
          <CurrentPlanCard
            subscription={subData}
            lastPayment={null}
            plans={PLANS_DATA?.find((p) => p.planId === subData?.planID)}
            currentPlan={currentPlan}
          />

          {/* All plans */}
          <PlanSelector
            plans={PLANS_DATA}
            selectedPlanId={selectedPlanId}
            currentPlanId={currentPlanId}
            billingCycle={billingCycle}
            onSelectPlan={handleSelectPlan}
            onCycleChange={setBillingCycle}
            billingCycles={billingCycles}
            allFeatures={allFeatures}
            recommendedPlanId={2}
          />
        </Container>
      </Box>

      <Box sx={{ mt: 2, textAlign: "center" }}>
        <Typography variant="caption" color="text.secondary">
          By subscribing, you agree to our{" "}
          <Link
            to="/terms-of-service"
            target="_blank"
            style={{
              color: "#371b17", // Your brand color
              textDecoration: "underline",
              fontWeight: 500,
            }}
          >
            Terms of Service
          </Link>
          .
        </Typography>
      </Box>

      {/* Payment modal */}
      {selectedPlan && (
        <PaymentModal
          open={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          selectedPlan={selectedPlan}
          billingCycle={billingCycle}
          restaurantName={restaurant.name}
          onSubmit={handlePaymentSubmit}
          selectedRestraunt={selectedRestaurant}
        />
      )}

      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setSuccessOpen(false)}
          sx={{ width: "100%", fontWeight: 600 }}
        >
          Subscription activated for {selectedRestaurant?.name}! 🎉
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default SubscriptionSetup;
